import { Suspense, lazy } from "react";
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router";

import Spinner from "@/components/spinner";
import { AppLayout } from "@/layouts/AppLayout";
import { AuthLayout } from "@/layouts/AuthLayout";
import { PrivateRoute, PublicRoute } from "@equidefi/portals/components/Routes";

import InvestmentAreaContainer from "@/areas/Investments/InvestmentAreaContainer";
import InvestmentContextProvider from "@/areas/Investments/InvestmentContext";
import IssuerContextProvider from "@/areas/Issuers/IssuerContext";
import OfferingAreaContainer from "@/areas/Offerings/OfferingAreaContainer";
import OfferingContextProvider from "@/areas/Offerings/OfferingContext";

import { IssuerChakraProvider } from "@/providers";
import { V2Theme } from "@equidefi/ui/theme";
import EditAccountType from "./areas/Investments/EditAccountType";
import EditIndividualForm from "./areas/Investments/EditIndividualForm";
import InvestmentAdditionalInfo from "./areas/Investments/InvestmentAdditionalInfo";
import ScrollToTop from "./components/ScrollToTop";

const VaultEdit = lazy(() => import("@/areas/Vault/Edit"));
const VaultOverview = lazy(() => import("@/areas/Vault/Overview"));
const VaultProfile = lazy(() => import("@/areas/Vault/Profile"));
const VaultAddress = lazy(() => import("@/areas/Vault/Address"));
const VaultEntity = lazy(() => import("@/areas/Vault/Entity"));
const VaultMetadata = lazy(() => import("@/areas/Vault/Metadata"));

const VaultInvestors = lazy(() => import("@/areas/Vault/InvestorsList"));

const Forgot = lazy(() => import("@/areas/Auth/Forgot"));
const AcceptInvitation = lazy(() => import("@/areas/Auth/AcceptInvitation"));
const Login = lazy(() => import("@/areas/Auth/Login"));
const MFA = lazy(() => import("@/areas/Auth/MFA"));
const AddPhone = lazy(() => import("@/areas/Auth/AddPhone"));
const Reset = lazy(() => import("@/areas/Auth/Reset"));

const BrokerList = lazy(() => import("@/areas/Brokers/BrokerList"));

const OnboardingLayout = lazy(() => import("@/areas/Onboarding/OnboardingLayout"));
const OnboardingCompanyInformation = lazy(() => import("@/areas/Onboarding/steps/CompanyInformation"));
const OnboardingOfferingInformation = lazy(() => import("@/areas/Onboarding/steps/OfferingInformation"));
const OnboardingBusinessAddress = lazy(() => import("@/areas/Onboarding/steps/BusinessAddress"));
const OnboardingBusinessContactInformation = lazy(() => import("@/areas/Onboarding/steps/BusinessContactInformation"));
const OnboardingOfferingDesign = lazy(() => import("@/areas/Onboarding/steps/OfferingDesign"));
const OnboardingInvestorPackage = lazy(() => import("@/areas/Onboarding/steps/InvestorPackage"));
const OnboardingEscrowAgent = lazy(() => import("@/areas/Onboarding/steps/EscrowAgent"));
const OnboardingTransferAgent = lazy(() => import("@/areas/Onboarding/steps/TransferAgent"));
const OnboardingPayoutInformation = lazy(() => import("@/areas/Onboarding/steps/PayoutInformation"));
const OnboardingPaymentInstructions = lazy(() => import("@/areas/Onboarding/steps/PaymentInstructions"));
const OnboardingReviewSubmit = lazy(() => import("@/areas/Onboarding/steps/ReviewSubmit"));
const OnboardingComplete = lazy(() => import("@/areas/Onboarding/components/OnboardingComplete"));
const StripeOnboarding = lazy(() => import("@/areas/StripeOnboarding/StripeOnboarding"));

const OfferingAdminContainer = lazy(() => import("@/areas/Offerings/OfferingAdminContainer"));
const AdminDetails = lazy(() => import("@/areas/Offerings/admin/AdminDetails"));
const AdminPayment = lazy(() => import("@/areas/Offerings/admin/AdminPayment"));
const AdminCommunications = lazy(() => import("@/areas/Offerings/admin/AdminCommunications"));
const AdminApiAccess = lazy(() => import("@/areas/Offerings/admin/AdminApiAccess"));
const AdminWebhooks = lazy(() => import("@/areas/Offerings/admin/AdminWebhooks"));
const AdminWebhookSubscription = lazy(() => import("@/areas/Offerings/admin/AdminWebhookSubscription"));
const AdminSubscriptionAgreements = lazy(() => import("@/areas/Offerings/admin/AdminSubscriptionAgreements"));
const AdminInvestorPackage = lazy(() => import("@/areas/Offerings/admin/AdminInvestorPackage"));
const AdminEscrowTransferAgent = lazy(() => import("@/areas/Offerings/admin/AdminEscrowTransferAgent"));
const AdminOfferingBrokers = lazy(() => import("@/areas/Offerings/admin/AdminOfferingBrokers"));
const OfferingAffiliates = lazy(() =>
  import("@/areas/Offerings/admin/OfferingAffiliates").then((m) => ({
    default: m.OfferingAffiliates,
  })),
);
const OfferingClosing = lazy(() => import("@/areas/Offerings/OfferingClosing"));
const OfferingList = lazy(() => import("@/areas/Offerings/OfferingListAll"));
const OfferingDetails = lazy(() => import("@/areas/Offerings/OfferingDetails"));
const OfferingInvestors = lazy(() => import("@/areas/Offerings/OfferingInvestors"));
const OfferingInvestments = lazy(() => import("@/areas/Offerings/OfferingInvestments"));
const OfferingAnalytics = lazy(() => import("@/areas/Offerings/OfferingAnalytics"));
const OfferingTranche = lazy(() => import("@/areas/Offerings/OfferingTranche"));
const OfferingTranches = lazy(() => import("@/areas/Offerings/OfferingTranches"));
const OfferingPayments = lazy(() => import("@/areas/Offerings/OfferingPayments"));
const OfferingPayouts = lazy(() => import("@/areas/Offerings/OfferingPayouts"));
const OfferingPayout = lazy(() => import("@/areas/Offerings/OfferingPayout"));
const OfferingPreClosing = lazy(() => import("@/areas/Offerings/OfferingPreClosing"));
const AgreementChangelogs = lazy(() => import("@/areas/Offerings/AgreementChangelogs"));
const AgreementChangelogDetail = lazy(() => import("@/areas/Offerings/AgreementChangelogDetail"));
const AgreementEditorPage = lazy(() => import("@/areas/Offerings/AgreementEditorPage"));

const InvestmentShow = lazy(() => import("@/areas/Investments/InvestmentShow"));
const InvestmentInvestors = lazy(() => import("@/areas/Investments/InvestmentInvestors"));
const InvestmentPayments = lazy(() => import("@/areas/Investments/InvestmentPayments"));
const InvestmentKyc = lazy(() => import("@/areas/Investments/InvestmentKyc"));
const InvestmentAgreements = lazy(() => import("@/areas/Investments/InvestmentAgreements"));
const InvestmentAccreditation = lazy(() => import("@/areas/Investments/InvestmentAccreditation"));
const InvestmentHistory = lazy(() => import("@/areas/Investments/InvestmentHistory"));

const Profile = lazy(() => import("@/areas/Profile/Profile"));

const IssuerList = lazy(() => import("@/areas/Issuers/IssuerList"));
const IssuerEdit = lazy(() => import("@/areas/Issuers/IssuerEdit"));
const IssuerOverview = lazy(() => import("@/areas/Issuers/IssuerOfferings"));

const LegacyRedirect = ({ children }) => {
  const params = useParams();

  return children(params);
};

const CobaltRoutes = () => {
  return (
    <Routes>
      <Route index element={<Navigate to="/offerings" replace={true} />} />
      <Route
        element={
          <PublicRoute fallbackUrl="/offerings">
            <AuthLayout />
          </PublicRoute>
        }>
        <Route path="login" element={<Login />} />
        <Route path="forgot" element={<Forgot />} />
        <Route path="mfa" element={<MFA />} />
        <Route path="reset/:id/:token" element={<Reset />} />
      </Route>
      <Route element={<AuthLayout />}>
        <Route path="accept-invite/issuer/:token" element={<AcceptInvitation />} />
      </Route>
      <Route
        element={
          <PrivateRoute fallbackUrl="/login">
            <AuthLayout />
          </PrivateRoute>
        }>
        <Route path="add-phone" element={<AddPhone />} />
      </Route>
      <Route path="stripe-onboarding/:authKey" element={<StripeOnboarding />} />
      <Route path="onboarding/complete" element={<OnboardingComplete />} />
      <Route path="onboarding/:authKey" element={<OnboardingLayout />}>
        <Route path="company-information" element={<OnboardingCompanyInformation />} />
        <Route path="offering-information" element={<OnboardingOfferingInformation />} />
        <Route path="business-address" element={<OnboardingBusinessAddress />} />
        <Route path="business-contact-information" element={<OnboardingBusinessContactInformation />} />
        <Route path="offering-design" element={<OnboardingOfferingDesign />} />
        <Route path="investor-package" element={<OnboardingInvestorPackage />} />
        <Route path="escrow-agent" element={<OnboardingEscrowAgent />} />
        <Route path="transfer-agent" element={<OnboardingTransferAgent />} />
        <Route path="payout-information" element={<OnboardingPayoutInformation />} />
        <Route path="payment-instructions" element={<OnboardingPaymentInstructions />} />
        <Route path="review" element={<OnboardingReviewSubmit />} />
      </Route>
      <Route
        element={
          <PrivateRoute fallbackUrl="/login">
            <AppLayout />
          </PrivateRoute>
        }>
        <Route
          path="offering/:offeringId/investment/:investmentId/*"
          element={
            <LegacyRedirect>
              {(params) => (
                <Navigate to={`/offerings/${params.offeringId}/investments/${params.investmentId}`} replace={true} />
              )}
            </LegacyRedirect>
          }
        />
        <Route
          path="offering/:offeringId/*"
          element={
            <LegacyRedirect>
              {(params) => <Navigate to={`/offerings/${params.offeringId}/investments`} replace={true} />}
            </LegacyRedirect>
          }
        />
        <Route
          path="offering/:offeringId/tranches"
          element={
            <LegacyRedirect>
              {(params) => <Navigate to={`/offerings/${params.offeringId}/closing`} replace={true} />}
            </LegacyRedirect>
          }
        />
        <Route
          path="offering/:offeringId/tranches/:trancheId"
          element={
            <LegacyRedirect>
              {(params) => (
                <Navigate to={`/offerings/${params.offeringId}/tranches/${params.trancheId}`} replace={true} />
              )}
            </LegacyRedirect>
          }
        />
        <Route path="vault/:vaultId" element={<VaultEdit />}>
          <Route path="overview" element={<VaultOverview />} />
          <Route path="profile" element={<VaultProfile />} />
          <Route path="address" element={<VaultAddress />} />
          <Route path="entity" element={<VaultEntity />} />
          <Route path="metadata" element={<VaultMetadata />} />
        </Route>
        <Route path="offerings">
          <Route index element={<OfferingList />} />

          <Route path=":offeringId" element={<OfferingContextProvider />}>
            <Route element={<OfferingAreaContainer />}>
              <Route index path="investments" element={<OfferingInvestments />} />
              <Route path="analytics" element={<OfferingAnalytics />} />
              <Route path="details" element={<OfferingDetails />} />
              <Route path="closing" element={<OfferingClosing />} />
              <Route path="tranches" element={<OfferingTranches />} />
              <Route path="profiles" element={<OfferingInvestors />} />
              <Route path="tranches/:trancheId" element={<OfferingTranche />} />
              <Route path="payments" element={<OfferingPayments />} />
              <Route path="payouts" element={<OfferingPayouts />} />
              <Route path="payouts/:payoutId" element={<OfferingPayout />} />
              <Route path="pre-closing" element={<OfferingPreClosing />} />
            </Route>
            <Route path="agreements/:agreementId/changelogs">
              <Route index element={<AgreementChangelogs />} />
              <Route path=":historyId" element={<AgreementChangelogDetail />} />
            </Route>
            <Route path="settings/agreements/:agreementId" element={<AgreementEditorPage />} />
            <Route path="admin" element={<OfferingAdminContainer />}>
              <Route index element={<Navigate to="./details" replace={true} />} />
              <Route path="details" element={<AdminDetails />} />
              <Route path="payment" element={<AdminPayment />} />
              <Route path="communications" element={<AdminCommunications />} />
              <Route path="api-access" element={<AdminApiAccess />} />
              <Route path="webhooks" element={<AdminWebhooks />} />
              <Route path="webhooks/:webhookId" element={<AdminWebhookSubscription />} />
              <Route path="subscription-agreements" element={<AdminSubscriptionAgreements />} />
              <Route path="investor-package" element={<AdminInvestorPackage />} />
              <Route path="escrow-transfer-agent" element={<AdminEscrowTransferAgent />} />
              <Route path="affiliates" element={<OfferingAffiliates />} />
              <Route path="offering-brokers" element={<AdminOfferingBrokers />} />
            </Route>
            <Route path="investments/:investmentId" element={<InvestmentContextProvider />}>
              <Route element={<InvestmentAreaContainer />}>
                <Route index element={<Navigate to="./summary" replace={true} />} />
                <Route path="summary" element={<InvestmentShow />} />
                <Route path="investors">
                  <Route index element={<InvestmentInvestors />} />
                  <Route path="account" element={<EditAccountType />} />
                  <Route path="new" element={<EditIndividualForm />} />
                  <Route path=":vaultId/edit" element={<EditIndividualForm />} />
                </Route>
                <Route path="payments" element={<InvestmentPayments />} />
                <Route path="kyc" element={<InvestmentKyc />} />
                <Route path="agreements" element={<InvestmentAgreements />} />
                <Route path="accreditation" element={<InvestmentAccreditation />} />
                <Route path="additional-info" element={<InvestmentAdditionalInfo />} />
                <Route path="history" element={<InvestmentHistory />} />
              </Route>
            </Route>
          </Route>
        </Route>
        <Route path="issuers" element={<IssuerContextProvider />}>
          <Route index element={<IssuerList />} />
          <Route path=":id" index element={<IssuerOverview />} />
          <Route path=":id/edit" element={<IssuerEdit />} />
        </Route>
        <Route path="brokers">
          <Route index element={<BrokerList />} />
        </Route>
        <Route path="account" element={<Profile />} />
        <Route path="vault" element={<VaultInvestors />} />
      </Route>
      <Route path="*" element={<Navigate to="/offerings" replace={true} />} />
    </Routes>
  );
};

const AppRoutes = ({ isAdmin }) => {
  return (
    <BrowserRouter>
      {/* Uses a hook under the hook. Some nuance may be needed page-to-page */}
      <ScrollToTop />
      <IssuerChakraProvider theme={V2Theme}>
        <Suspense fallback={<Spinner show />}>
          <CobaltRoutes />
        </Suspense>
      </IssuerChakraProvider>
    </BrowserRouter>
  );
};

export default AppRoutes;
